import React, { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { hasSellerPermission, sellerAuthAPI, type SellerAuthProfile, type SellerSession } from '../utils/sellerApi'
import { queueLoginDisclaimer } from '../../../src/shared/loginDisclaimer'

type Seller = SellerAuthProfile

interface SellerAuthContextType {
  seller: Seller | null
  session: SellerSession | null
  token: string | null
  login: (email: string, password: string) => Promise<Seller>
  requestStaffOtp: (email: string) => Promise<string>
  verifyStaffOtp: (email: string, otp: string) => Promise<Seller>
  logout: () => void
  refreshSeller: () => Promise<Seller | null>
  loading: boolean
  isAuthenticated: boolean
  hasOperationalAccess: boolean
  needsPlanActivation: boolean
  isStaffSession: boolean
  permissions: string[]
  hasPermission: (requiredPermission: string) => boolean
}

const SELLER_TOKEN_KEY = 'seller_token'
const SELLER_PROFILE_KEY = 'seller_profile'
const SELLER_SESSION_KEY = 'seller_session'
const SELLER_BOOTSTRAP_TIMEOUT_MS = 8000

const SellerAuthContext = createContext<SellerAuthContextType | undefined>(undefined)

const clearSellerSession = () => {
  localStorage.removeItem(SELLER_TOKEN_KEY)
  localStorage.removeItem(SELLER_PROFILE_KEY)
  localStorage.removeItem(SELLER_SESSION_KEY)
}

const persistSellerProfile = (seller: Seller | null) => {
  if (!seller) {
    localStorage.removeItem(SELLER_PROFILE_KEY)
    return
  }

  localStorage.setItem(SELLER_PROFILE_KEY, JSON.stringify(seller))
}

const readCachedSellerProfile = (): Seller | null => {
  const raw = localStorage.getItem(SELLER_PROFILE_KEY)

  if (!raw) {
    return null
  }

  try {
    return JSON.parse(raw) as Seller
  } catch {
    localStorage.removeItem(SELLER_PROFILE_KEY)
    return null
  }
}

const persistSellerSession = (session: SellerSession | null) => {
  if (!session) {
    localStorage.removeItem(SELLER_SESSION_KEY)
    return
  }

  localStorage.setItem(SELLER_SESSION_KEY, JSON.stringify(session))
}

const readCachedSellerSession = (): SellerSession | null => {
  const raw = localStorage.getItem(SELLER_SESSION_KEY)

  if (!raw) {
    return null
  }

  try {
    return JSON.parse(raw) as SellerSession
  } catch {
    localStorage.removeItem(SELLER_SESSION_KEY)
    return null
  }
}

const ownerSessionFallback: SellerSession = {
  is_staff_session: false,
  permissions: ['*'],
  staff: null,
}

const normalizeSession = (session: SellerSession | null | undefined): SellerSession => {
  if (!session) {
    return ownerSessionFallback
  }

  return {
    is_staff_session: Boolean(session.is_staff_session),
    permissions: Array.isArray(session.permissions) ? session.permissions : [],
    staff: session.staff ?? null,
  }
}

const withTimeout = async <T,>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined

  const timeoutPromise = new Promise<T>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error('Seller auth bootstrap timed out'))
    }, timeoutMs)
  })

  try {
    return await Promise.race([promise, timeoutPromise])
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
  }
}

const getErrorStatus = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null) {
    return undefined
  }

  const maybeResponse = (error as { response?: { status?: number } }).response
  return maybeResponse?.status
}

export const SellerAuthProvider = ({ children }: { children: ReactNode }) => {
  const [seller, setSeller] = useState<Seller | null>(() => readCachedSellerProfile())
  const [session, setSession] = useState<SellerSession | null>(() => readCachedSellerSession() ?? ownerSessionFallback)
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(SELLER_TOKEN_KEY))
  const [loading, setLoading] = useState(true)

  const refreshSeller = async (): Promise<Seller | null> => {
    const response = await withTimeout(sellerAuthAPI.profile(), SELLER_BOOTSTRAP_TIMEOUT_MS)
    const sellerData = response?.data?.seller ?? null
    const nextSession = normalizeSession(response?.data?.session)

    if (!sellerData) {
      persistSellerProfile(null)
      persistSellerSession(null)
      setSeller(null)
      setSession(ownerSessionFallback)
      return null
    }

    persistSellerProfile(sellerData)
    persistSellerSession(nextSession)
    setSeller(sellerData)
    setSession(nextSession)

    return sellerData
  }

  useEffect(() => {
    let active = true

    const bootstrapSeller = async () => {
      const savedToken = localStorage.getItem(SELLER_TOKEN_KEY)
      const cachedSeller = readCachedSellerProfile()
      const cachedSession = readCachedSellerSession() ?? ownerSessionFallback

      if (!savedToken) {
        if (active) {
          setToken(null)
          setSeller(null)
          setLoading(false)
        }
        return
      }

      if (active) {
        setToken(savedToken)
        if (cachedSeller) {
          setSeller(cachedSeller)
        }
          setSession(cachedSession)
      }

      try {
        const sellerData = await refreshSeller()

        if (active) {
          setSeller(sellerData)
        }
      } catch (error) {
        const status = getErrorStatus(error)

        if (status === 401) {
          clearSellerSession()

          if (active) {
            setToken(null)
            setSeller(null)
            setSession(ownerSessionFallback)
          }
        } else if (!cachedSeller && active) {
          setSeller(null)
          setSession(ownerSessionFallback)
        }
      } finally {
        if (active) {
          setLoading(false)
        }
      }
    }

    void bootstrapSeller()

    return () => {
      active = false
    }
  }, [])

  const login = async (email: string, password: string) => {
    const response = await sellerAuthAPI.login(email, password)
    const { access_token, seller: sellerData, session: sessionData } = response.data
    const normalizedSession = normalizeSession(sessionData)

    queueLoginDisclaimer('seller')
    localStorage.setItem(SELLER_TOKEN_KEY, access_token)
    persistSellerProfile(sellerData)
    persistSellerSession(normalizedSession)
    setToken(access_token)
    setSeller(sellerData)
    setSession(normalizedSession)
    setLoading(false)

    return sellerData
  }

  const requestStaffOtp = async (email: string) => {
    const response = await sellerAuthAPI.requestStaffOtp(email)
    return response.data?.message || 'OTP sent to your email'
  }

  const verifyStaffOtp = async (email: string, otp: string) => {
    const response = await sellerAuthAPI.verifyStaffOtp(email, otp)
    const { access_token, seller: sellerData, session: sessionData } = response.data
    const normalizedSession = normalizeSession(sessionData)

    queueLoginDisclaimer('seller')
    localStorage.setItem(SELLER_TOKEN_KEY, access_token)
    persistSellerProfile(sellerData)
    persistSellerSession(normalizedSession)
    setToken(access_token)
    setSeller(sellerData)
    setSession(normalizedSession)
    setLoading(false)

    return sellerData
  }

  const logout = async () => {
    try {
      await sellerAuthAPI.logout()
    } catch {
      // Ignore logout errors while still clearing the local seller session.
    } finally {
      clearSellerSession()
      setToken(null)
      setSeller(null)
      setSession(ownerSessionFallback)
      setLoading(false)
      window.location.href = '/seller/login'
    }
  }

  const hasOperationalAccess = Boolean(seller?.access_state?.has_operational_access)
  const needsPlanActivation = Boolean(seller?.access_state?.requires_plan_activation)
  const permissions = session?.permissions ?? ownerSessionFallback.permissions
  const isStaffSession = Boolean(session?.is_staff_session)
  const hasPermission = (requiredPermission: string) => hasSellerPermission(permissions, requiredPermission)

  const value = {
    seller,
    session,
    token,
    login,
    requestStaffOtp,
    verifyStaffOtp,
    logout,
    refreshSeller,
    loading,
    isAuthenticated: Boolean(seller && token),
    hasOperationalAccess,
    needsPlanActivation,
    isStaffSession,
    permissions,
    hasPermission,
  }

  return <SellerAuthContext.Provider value={value}>{children}</SellerAuthContext.Provider>
}

export const useSellerAuth = () => {
  const context = useContext(SellerAuthContext)

  if (context === undefined) {
    throw new Error('useSellerAuth must be used within SellerAuthProvider')
  }

  return context
}

